import axios from 'src/utils/axios';
import parse from 'src/utils/parse';
import logger from 'src/utils/logger';
import {
  BackupsDashboardData,
  BackupSetsGraphResponse,
  BackupSetsListResponse,
  BackupSetDetailsApiResponse,
  QuotaHistoryResponse,
  BackupsetHistoryResponse,
  BackupJobsApiResponse,
  RunBackupSetResponse,
} from 'src/types/mindtime/backups';

function parseFilenameFromContentDisposition(
  header: string | undefined
): string | null {
  if (!header) {
    return null;
  }
  const utf8Match = header.match(/filename\*=(?:UTF-8'')?([^;\s]+)/i);
  if (utf8Match) {
    const raw = utf8Match[1].replace(/['"]/g, '');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const asciiMatch = header.match(/filename\s*=\s*("?)([^";\n]+)\1/i);
  return asciiMatch ? asciiMatch[2].trim() : null;
}

class MindtimeBackupsApi {
  private appendOrganizationId(url: string, organizationId?: string): string {
    if (!organizationId) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}organizationId=${encodeURIComponent(organizationId)}`;
  }

  async getDashboard(organizationId?: string): Promise<BackupsDashboardData> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(this.appendOrganizationId(`${baseUrl}/ahsay-user`, organizationId))
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data;
            if (!data) {
              throw new Error('Invalid response: missing data property');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getBackupSetsGraph(organizationId?: string): Promise<BackupSetsGraphResponse> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(
          this.appendOrganizationId(
            `${baseUrl}/ahsay-user/backup-sets/graph`,
            organizationId
          )
        )
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data;
            if (!data) {
              throw new Error('Invalid response: missing data property');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getBackupSetsList(organizationId?: string): Promise<BackupSetsListResponse> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      const requestUrlWithOrg = this.appendOrganizationId(
        `${baseUrl}/ahsay-user/backup-sets`,
        organizationId
      );
      const requestUrl = `${requestUrlWithOrg}${
        requestUrlWithOrg.includes('?') ? '&' : '?'
      }_ts=${Date.now()}`;

      axios
        .get(requestUrl, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        })
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data;
            if (!data) {
              throw new Error('Invalid response: missing data property');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getBackupSetDetails(
    backupSetId: string,
    organizationId?: string
  ): Promise<BackupSetDetailsApiResponse> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(
          this.appendOrganizationId(
            `${baseUrl}/ahsay-user/backup-sets/${backupSetId}`,
            organizationId
          )
        )
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data;
            if (!data) {
              throw new Error('Invalid response: missing data property');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getQuotaHistory(
    from: string,
    to: string,
    organizationId?: string
  ): Promise<QuotaHistoryResponse> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(
          this.appendOrganizationId(
            `${baseUrl}/ahsay-user/quota-distribution?from=${encodeURIComponent(
              from
            )}&to=${encodeURIComponent(to)}`,
            organizationId
          )
        )
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data;
            if (!data) {
              throw new Error('Invalid response: missing data property');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getBackupsetHistory(
    from: string,
    to: string,
    organizationId?: string
  ): Promise<BackupsetHistoryResponse> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(
          this.appendOrganizationId(
            `${baseUrl}/ahsay-user/backupsetHistoryGraph?from=${encodeURIComponent(
              from
            )}&to=${encodeURIComponent(to)}`,
            organizationId
          )
        )
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data;
            if (!data) {
              throw new Error('Invalid response: missing data property');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getBackupJobs(backupSetId: string, days: number): Promise<BackupJobsApiResponse> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      const safeDays = Number.isFinite(days) && days > 0 ? days : 7;

      axios
        .get(
          `${baseUrl}/backup-jobs?backupSetId=${encodeURIComponent(
            backupSetId
          )}&days=${encodeURIComponent(String(safeDays))}`
        )
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data;
            if (!data || !Array.isArray(data)) {
              throw new Error('Invalid response: backup-jobs API must return an array');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async refreshBackupJobs(
    backupSetId: string,
    days: number
  ): Promise<BackupJobsApiResponse> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      const safeDays = Number.isFinite(days) && days > 0 ? days : 7;

      axios
        .get(
          `${baseUrl}/backup-jobs/refresh?backupSetId=${encodeURIComponent(
            backupSetId
          )}&days=${encodeURIComponent(String(safeDays))}`
        )
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data;
            if (!data || !Array.isArray(data)) {
              throw new Error('Invalid response: backup-jobs refresh API must return an array');
            }
            resolve(data);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async exportBackupJobReportsCurrentDay(backupSetId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      if (!backupSetId) {
        const error = new Error('backupSetId is required to export backup job reports');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .post(`${baseUrl}/sync/backup-jobs/export-reports/current-day`, {
          backupSetId,
        })
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            resolve();
          } else {
            reject(new Error(`Export reports failed with status ${response.status}`));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async downloadBackupJobReport(
    backupJobId: string
  ): Promise<{ blob: Blob; filename: string | null }> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      if (!backupJobId) {
        const error = new Error('backupJobId is required to download report');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(
          `${baseUrl}/sync/backup-jobs/${encodeURIComponent(backupJobId)}/download`,
          { responseType: 'blob' }
        )
        .then(async (response) => {
          if (response.status >= 200 && response.status < 300) {
            if (response.data) {
              const filename = parseFilenameFromContentDisposition(
                response.headers['content-disposition'] as string | undefined
              );
              resolve({ blob: response.data, filename });
            } else {
              reject(new Error('Failed to download backup job report'));
            }
          } else {
            try {
              const text = await response.data.text();
              reject(new Error(text || 'Failed to download backup job report'));
            } catch {
              reject(
                new Error('Failed to download backup job report. The server returned an error.')
              );
            }
          }
        })
        .catch(async (error) => {
          if (error.response && error.response.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              try {
                const errorData = JSON.parse(text);
                reject(
                  new Error(
                    errorData.message || text || 'Failed to download backup job report'
                  )
                );
              } catch {
                reject(new Error(text || 'Failed to download backup job report'));
              }
            } catch {
              reject(new Error('Failed to download backup job report. Please try again.'));
            }
          } else if (error.message) {
            reject(new Error(error.message));
          } else {
            reject(error);
          }
        });
    });
  }

  async runBackupSet(
    backupSetId: string,
    organizationId?: string
  ): Promise<RunBackupSetResponse> {
    return new Promise((resolve, reject) => {
      const baseUrl = process.env.REACT_APP_BSI;
      if (!baseUrl) {
        const error = new Error('REACT_APP_BSI environment variable is not defined');
        logger(error, 'error');
        reject(error);
        return;
      }

      if (!backupSetId) {
        const error = new Error('backupSetId is required to run backup');
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .post(
          this.appendOrganizationId(
            `${baseUrl}/ahsay-user/backup-sets/${encodeURIComponent(backupSetId)}/run`,
            organizationId
          )
        )
        .then((response) => {
          try {
            const parsedResponse = parse(response);
            const data = parsedResponse?.data;
            if (!data) {
              throw new Error('Invalid response: missing data property');
            }
            resolve(data as RunBackupSetResponse);
          } catch (parseError) {
            logger(parseError, 'error');
            reject(parseError);
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }
}

export const mindtimeBackupsApi = new MindtimeBackupsApi();
